import { Grid, LinearProgress } from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import { MeetingRoom } from "@material-ui/icons";
import React, { FC, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import { routesPaths } from "../../Routes";
import {
  useCreateRoomAssignmentMutation,
  useDeleteRoomAssignmentMutation,
  useGetExamSessionQuery,
} from "../../api/rtk/ExamSessionApi";
import { NewEntityId } from "../../data/constants";
import { ExamDayType } from "../../model/ExamDayType";
import { RoomAssignmentViewModel } from "../../model/RoomAssignmentViewModel";
import TitleWithIcon from "../common/TitleWithIcon";
import DefaultButtons from "../common/buttons/DefaultButtons";
import LocaleFormatDate from "../common/dates/LocaleFormatDate";
import { IRoomAssignmentErrors, RoomAssignmentFields } from "./RoomAssignmentFields";

interface RouteParameters {
  examSessionId: string;
  locationId: string;
  examDayId: string;
  roomAssignmentId: string;
}

const RoomAssignmentForm: FC = () => {
  const history = useHistory();
  const { examSessionId, locationId, examDayId, roomAssignmentId } = useParams<RouteParameters>();
  const { data: examSession, isFetching: isExamSessionFetching } = useGetExamSessionQuery(examSessionId);
  const [addRoomAssignment, { isLoading: isAddRoomAssignmentLoading }] = useCreateRoomAssignmentMutation();
  const [deleteRoomAssignment, { isLoading: isDeleteRoomAssignmentLoading }] = useDeleteRoomAssignmentMutation();
  const examDay = examSession?.examDays.find((examDay) => examDay.id === examDayId);
  const location = examSession?.locations.find((location) => location.id === locationId);

  const [roomAssignment, setRoomAssignment] = useState<RoomAssignmentViewModel>({
    id: NewEntityId,
    languages: [],
    roomId: "",
    isPreparationRoom: false,
    assignedUsers: [],
    examAssignments: [],
  });

  const [errors, setErrors] = useState<IRoomAssignmentErrors>({});

  useEffect(() => {
    const initialRoomAssignment = examDay?.roomAssignments.find(
      (roomAssignment) => roomAssignment.id === roomAssignmentId
    );
    if (initialRoomAssignment !== undefined) {
      setRoomAssignment(initialRoomAssignment);
    }
  }, [roomAssignmentId, examDay]);

  if (isExamSessionFetching || isAddRoomAssignmentLoading || isDeleteRoomAssignmentLoading) {
    return <LinearProgress variant="query" />;
  }

  if (examSession === undefined || examDay === undefined || location === undefined) {
    return <></>;
  }

  const examDayNeedsPreparationRoom = examDay.type === ExamDayType.Oral;
  const examDayHasPreparationRoom = !examDay.roomAssignments.every(
    (roomAssignment) => !roomAssignment.isPreparationRoom
  );
  const examDayRoomsIds = examDay.roomAssignments.map((roomAssignment) => roomAssignment.roomId);
  const examDayNeedsPcRoom = examDay.type === ExamDayType.Written || examDay.type === ExamDayType.Correction;
  const unassignedRooms = location.rooms.filter(
    (room) => room.isPcRoom === examDayNeedsPcRoom && !examDayRoomsIds.includes(room.id)
  );

  const validate = () => {
    if (!roomAssignment.roomId) {
      setErrors({
        room: true,
      });
      return false;
    }

    return true;
  };

  const close = () => history.push(routesPaths.examDay(examSessionId, locationId, examDayId));

  const handleChange = (partialRoomAssignment: Partial<RoomAssignmentViewModel>) =>
    setRoomAssignment((prevRoomAssignment) => ({ ...prevRoomAssignment, ...partialRoomAssignment }));

  const handleClose = () => close();

  const handleSave = async () => {
    if (!validate()) {
      return;
    }

    const response = await addRoomAssignment({ examSessionId, examDayId, data: roomAssignment }).unwrap();
    if (response.success) {
      close();
    }
  };

  const handleDelete = async () => {
    const response = await deleteRoomAssignment({
      examSessionId,
      examDayId,
      roomAssignmentId: roomAssignment.id,
    }).unwrap();
    if (response.success) {
      close();
    }
  };

  const handleLanguageSelectChange = (selectedLanguages: string[]) => {
    setRoomAssignment((prevRoomAssignment) => ({ ...prevRoomAssignment, languages: [...selectedLanguages] }));
  };

  return (
    <>
      <Grid container={true} spacing={0} alignItems="baseline">
        <Grid item={true} xs={12} sm={6}>
          <Typography variant="h6" component="span">
            {location.name} - <LocaleFormatDate date={examDay.date} />
          </Typography>
          <TitleWithIcon title={examDay.type} variant="h4">
            <MeetingRoom />
          </TitleWithIcon>
        </Grid>
        <Grid item={true} xs={12} sm={6}>
          <DefaultButtons
            onClose={handleClose}
            onDelete={handleDelete}
            onSave={handleSave}
            isDeleteButtonEnabled={roomAssignment.id !== NewEntityId}
          />
        </Grid>
      </Grid>
      <RoomAssignmentFields
        showPreparationRoomSwitch={examDayNeedsPreparationRoom}
        disablePreparationRoomSwitch={examDayHasPreparationRoom}
        availableLanguages={location.languageRegions}
        onSelectedLanguagesChanged={handleLanguageSelectChange}
        rooms={unassignedRooms}
        roomAssignment={roomAssignment}
        onPropertyValueChanges={handleChange}
        errors={errors}
      />
    </>
  );
};

export default RoomAssignmentForm;
